import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import VideoModal, { IVideoModalProps } from '../VideoModal';

export interface IVideoShowcaseProps extends IBaseElementProps {
	style:
		| 'Vertical'
		| 'Horizontal'
		| 'Horizontal-Reverse'
		| 'Vertical-Reverse';
	backgroundColor?: 'none' | 'light' | 'themed';
	heading?: string;
	/**
	 * Content
	 * @control richtext
	 */
	content?: string;
	cta?: ILink;
	video?: IVideoModalProps;
	videoCaption?: string;
	captionPosition?: 'start' | 'center' | 'end';
	videoStyle: 'modal' | 'inline';
	inverted?: boolean;
}

/**
 * Video Showcase
 * @block
 * @icon video-alt
 */
const VideoShowcase: React.FC<IVideoShowcaseProps> = ({
	id,
	className,
	classNames = [],
	style = 'Vertical',
	heading,
	video,
	backgroundColor = 'none',
	videoCaption,
	captionPosition = 'start',
	videoStyle = 'modal',
	content,
	cta,
	inverted = false,
}) => {
	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'video-showcase px-3 py-5 py-lg-8',
					backgroundColor !== 'none' && `overlay-${backgroundColor}`,
					!heading && !content && !cta?.label && 'no-content',
					...classNames,
				)
			}
		>
			<div
				className={cn(
					`video-showcase__container align-items-center mw-1240px d-flex gap-5`,
					style === 'Vertical'
						? 'flex-column'
						: style === 'Horizontal'
						? 'flex-column flex-lg-row align-items-lg-start'
						: style === 'Vertical-Reverse'
						? 'flex-column-reverse'
						: 'flex-column-reverse flex-lg-row-reverse align-items-lg-start',
				)}
			>
				{(heading || content || cta?.label) && (
					<div
						className={cn(
							`video-showcase__content ${
								style === 'Horizontal' ||
								style === 'Horizontal-Reverse'
									? 'align-items-center align-items-lg-start horizontal-sizing'
									: 'align-items-center'
							} d-inline-flex flex-column gap-3`,
						)}
					>
						{heading && (
							<h2
								className={cn(
									'video-showcase__heading mb-0',
									backgroundColor === 'themed' &&
										'text-white',
									'text-center',
									style === 'Horizontal' && 'text-md-left',
								)}
							>
								{heading}
							</h2>
						)}
						{content && (
							<div className="video-showcase__copy">
								<div
									className={cn(
										backgroundColor === 'themed' &&
											'text-white',
										'text-center',
										(style === 'Horizontal' ||
											style === 'Horizontal-Reverse') &&
											'text-lg-start',
									)}
									dangerouslySetInnerHTML={{
										__html: content,
									}}
								/>
							</div>
						)}
						{cta?.label && (
							<Button
								{...cta}
								className={`video-showcase__cta mt-3 btn btn-${
									inverted
										? 'secondary'
										: backgroundColor == 'themed'
										? 'light'
										: 'primary'
								}`}
							/>
						)}
					</div>
				)}

				{video && (
					<div
						className={cn(
							'video-showcase__video-wrapper flex-grow-1 flex-shrink-0',
							'w-100',
							(style === 'Horizontal' ||
								style === 'Horizontal-Reverse') &&
								'w-lg-auto',
						)}
					>
						<VideoModal
							className="video-showcase__video mx-auto rounded-5"
							{...video}
							style={videoStyle}
						/>
						{videoCaption && (
							<div
								className={`video-showcase__caption text-${captionPosition}`}
							>
								<span>{videoCaption}</span>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default VideoShowcase;
